import React, { useState } from "react";
import { Card, Badge, Tooltip, Button } from "flowbite-react";
import {
  HiBuildingOffice2,
  HiCalendarDays,
  HiCurrencyDollar,
  HiChevronDown,
  HiChevronUp,
  HiArrowTopRightOnSquare,
  HiMapPin,
} from "react-icons/hi2";
import TitleStripe from "../../common/TitleStripe";
import NoOcurrence from "../../common/query/NoOcurrence";
import Emails from "../../common/query/Emails";
import Telefones from "../../common/query/Telefones";
import Sociedade from "../../common/query/Sociedade";
import { formatDocument, formatToReal } from "../../../utils/utils";
import moment from "moment";
import { HiClipboard } from "react-icons/hi";

const DadosReceitaFederalPJ = ({ data }) => {
  const pj = data?.dadosCadastrais || data;
  const [showMore, setShowMore] = useState(false);

  // ===== Endereço (helpers locais) =====
  const cepFmt = String(pj?.cep || "")
    .replace(/\D/g, "")
    .replace(/^(\d{5})(\d{0,3}).*/, (_, a, b) => (b ? `${a}-${b}` : a));

  const addressLine = [
    pj?.logradouro || pj?.endereco,
    pj?.numero,
    pj?.complemento,
  ]
    .filter(Boolean)
    .join(", ");

  const cityLine = [
    pj?.bairro && pj?.bairro,
    (pj?.cidade || pj?.municipio) &&
      (pj?.uf
        ? `${pj?.cidade || pj?.municipio}/${pj?.uf}`
        : pj?.cidade || pj?.municipio),
  ]
    .filter(Boolean)
    .join(" • ");

  const fullAddress = [addressLine, cityLine, cepFmt && `CEP ${cepFmt}`]
    .filter(Boolean)
    .join(" • ");

  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    fullAddress
  )}`;

  async function handleCopyAddress() {
    try {
      await navigator.clipboard.writeText(fullAddress || "");
    } catch {}
  }

  if (!pj) return <NoOcurrence />;

  const situacaoAtiva = pj.situacao?.toUpperCase() === "ATIVA";

  return (
    <div className="border border-gray-200 shadow-sm bg-gray-50 rounded-2xl p-5 space-y-5 mt-5">
      {/* HEADER / TÍTULO */}
      <div className="flex flex-row items-center justify-between gap-3">
        <div>
          <div className="flex gap-4 items-start">
            <img className="w-14" src="/assets/logo_receita.png" />
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                Dados da Receita Federal
              </h2>
              <p className="text-sm text-gray-500">
                Informações oficiais de cadastro da empresa
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ================== DADOS CADASTRAIS ================== */}
      <div className="bg-white p-3 rounded-lg border border-gray-200">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 text-base">
          <div>
            <p className="text-gray-500 font-medium">Razão Social</p>
            <p className="text-gray-900 font-semibold text-sm flex items-center gap-2">
              {pj.razaoSocial || "Indisponível"}
            </p>
          </div>

          <div>
            <p className="text-gray-500 font-medium">CNPJ</p>
            <p className="text-gray-900 font-semibold">
              {formatDocument(pj.cnpj) || "Indisponível"}
            </p>
          </div>

          <div>
            <p className="text-gray-500 font-medium">Natureza Jurídica</p>
            <p className="text-gray-900">
              {pj.naturezaJuridica || "Indisponível"}
            </p>
          </div>

          <div>
            <p className="text-gray-500 font-medium">Data de Fundação</p>
            <div className="flex items-center gap-2 text-gray-900">
              <HiCalendarDays className="text-indigo-600" />
              <span>
                {pj.dataFundacao
                  ? moment(pj.dataFundacao).format("DD/MM/yyyy")
                  : "Indisponível"}
              </span>
            </div>
          </div>

          <div>
            <p className="text-gray-500 font-medium">Capital Social</p>
            <div className="flex items-center gap-2 text-gray-900">
              <HiCurrencyDollar className="text-green-600" />
              <span>
                {formatToReal(pj.capitalSocial || pj.capitalInicial) ||
                  "Indisponível"}
              </span>
            </div>
          </div>

          <div>
            <p className="text-gray-500 font-medium">Porte da Empresa</p>
            <p className="text-gray-900">{pj.porteEmpresa || "Indisponível"}</p>
          </div>

          <div>
            <p className="text-gray-500 font-medium">Opção Simples</p>
            <p
              className={`font-semibold ${
                pj.opcaoSimples ? "text-green-600" : "text-gray-600"
              }`}
            >
              {pj.opcaoSimples ? (
                <Badge className="w-fit" color="success">
                  Sim
                </Badge>
              ) : (
                <Badge className="w-fit" color="failure">
                  Não
                </Badge>
              )}
            </p>
          </div>

          <div>
            <p className="text-gray-500 font-medium">Opção MEI</p>
            <p
              className={`font-semibold ${
                pj.opcaoMei ? "text-green-600" : "text-gray-600"
              }`}
            >
              {pj.opcaoMei ? (
                <Badge className="w-fit" color="success">
                  Sim
                </Badge>
              ) : (
                <Badge className="w-fit" color="failure">
                  Não
                </Badge>
              )}
            </p>
          </div>
        </div>
        <div className="bg-white border border-gray-200 p-3 rounded-lg mt-4">
          <div className="flex items-center gap-3">
            <HiMapPin className="text-indigo-600 w-5 h-5 shrink-0" />

            <p className="text-gray-800 text-base flex-1">
              {fullAddress || "Indisponível"}
            </p>

            <div className="flex gap-2 shrink-0">
              <Button color="light" size="xs" onClick={handleCopyAddress}>
                <HiClipboard className="w-4 h-4" />
              </Button>

              {fullAddress && (
                <a href={mapsUrl} target="_blank" rel="noreferrer">
                  <Button color="light" size="xs">
                    <HiArrowTopRightOnSquare className="w-4 h-4" />
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ================== SÓCIOS ================== */}
      {pj.socios && (
        <div className=" border-t border-gray-100">
          <TitleStripe isGray>Sócios</TitleStripe>
          {pj.socios.length > 0 ? (
            <Sociedade socios={pj.socios} canFormatDocument={false} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {/* ================== SEÇÃO EXPANDIDA ================== */}
      {showMore && (
        <div className="space-y-6 animate-fadeIn pt-2 border-t border-gray-100">
          {/* EMAILS */}
          {pj.email && (
            <div>
              <TitleStripe isGray>E-mail</TitleStripe>
              <Emails emails={[{ email: pj.email }]} />
            </div>
          )}

          {/* TELEFONES */}
          {pj.telefones && (
            <div>
              <TitleStripe isGray>Telefones</TitleStripe>
              {pj.telefones.length > 0 ? (
                <Telefones telefones={pj.telefones} />
              ) : (
                <NoOcurrence />
              )}
            </div>
          )}
        </div>
      )}

      {/* ================== BOTÃO "VER MAIS" ================== */}
      <div className="text-center pt-2">
        <Button
          color="light"
          onClick={() => setShowMore(!showMore)}
          className="text-sm font-medium flex items-center justify-center gap-2 mx-auto"
        >
          <div className="flex items-center gap-2">
            {showMore ? (
              <>
                <span>Ocultar detalhes </span>
                <HiChevronUp className="w-4 h-4" />
              </>
            ) : (
              <>
                <span>Ver mais informações </span>
                <HiChevronDown className="w-4 h-4" />
              </>
            )}
          </div>
        </Button>
      </div>
    </div>
  );
};

export default DadosReceitaFederalPJ;
